import { ROBOT_MODES } from 'utils/constants'
import { IModeSwitchPending } from 'utils/interfaces'

export const MODE_SWITCH_CONFIRM_FALLBACK_MS = 10_000
/** Map recording often needs longer than the 10s pending window before MCS confirms. */
export const MAP_RECORDING_CONFIRM_FALLBACK_MS = 25_000

export type ModeSwitchConfirmSnapshot = {
  activeMode?: ROBOT_MODES | null
  pending?: Pick<IModeSwitchPending, 'requestedAt' | 'timeoutSec'> | null
}

/** Remaining wait for the first MCS feedback that enters the requested mode. */
export function modeSwitchConfirmWaitMs(
  pending: Pick<IModeSwitchPending, 'requestedAt' | 'timeoutSec'> | null | undefined,
  now = Date.now(),
  minimumMs = 0
): number {
  const timeoutMs = (pending?.timeoutSec && pending.timeoutSec > 0 ? pending.timeoutSec : 10) * 1000
  const floorMs = Math.max(timeoutMs, minimumMs)
  if (!pending?.requestedAt) {
    return floorMs
  }
  const started = Date.parse(pending.requestedAt)
  if (!Number.isFinite(started)) {
    return floorMs
  }
  return Math.max(0, floorMs - (now - started))
}

/**
 * Resolves on the first transition into targetMode, when mode-switch pending
 * clears without that mode, or when the wait budget elapses.
 *
 * When `keepWaitingAfterPendingClear` is set, a cleared pending stamp alone does
 * not fail the wait — useful when the robot can enter the mode after the server
 * pending window.
 */
export function waitForModeSwitchConfirmation(
  targetMode: ROBOT_MODES,
  read: () => ModeSwitchConfirmSnapshot,
  options?: {
    fallbackMs?: number
    intervalMs?: number
    isAborted?: () => boolean
    keepWaitingAfterPendingClear?: boolean
  }
): Promise<'confirmed' | 'timeout' | 'aborted'> {
  const fallbackMs = options?.fallbackMs ?? MODE_SWITCH_CONFIRM_FALLBACK_MS
  const intervalMs = options?.intervalMs ?? 200
  const isAborted = options?.isAborted ?? (() => false)
  const keepWaitingAfterPendingClear = options?.keepWaitingAfterPendingClear ?? false
  return new Promise(resolve => {
    const started = Date.now()
    let sawPending = Boolean(read().pending)
    const tick = () => {
      if (isAborted()) {
        resolve('aborted')
        return
      }
      const snapshot = read()
      if (snapshot.activeMode === targetMode) {
        resolve('confirmed')
        return
      }
      if (snapshot.pending) {
        sawPending = true
      } else if (sawPending && !keepWaitingAfterPendingClear) {
        resolve('timeout')
        return
      }
      if (snapshot.pending) {
        const budget = modeSwitchConfirmWaitMs(snapshot.pending, Date.now(), fallbackMs)
        if (budget <= 0) {
          resolve('timeout')
          return
        }
      } else if (Date.now() - started >= fallbackMs) {
        resolve('timeout')
        return
      }
      window.setTimeout(tick, intervalMs)
    }
    tick()
  })
}
